import { useEffect, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

import {
	ONBOARDING_EXPERIENCES,
	TEMPLATES_COLLECTION,
} from '../../constants/onboarding-state/onboarding-constants';
import {
	useGetOnboardingState,
	deserializeState,
	useSetOnboardingState,
} from '../onboardingState/onboardingState';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { UserPersonalizationQuery } from '../useOnboardingEligible/UserPersonalizationQuery.experimentalgraphql';
import type {
	PersonalizationQuery,
	PersonalizationQueryVariables,
} from '../useOnboardingEligible/__types__/UserPersonalizationQuery';
import { useIsEstablishedUserTraitName } from '../useIsEstablishedUserTraitName';
import { useExperimentalUserPersonalizationQuery } from '../useExperimentalUserPersonalizationQuery';

import { GetProductsTeamAndSpaceQuery } from './GetProductsTeamAndSpaceQuery.graphql';

export const useTemplatesCollectionEligible = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isUserEligibleForTemplatesCollection, setisUserEligibleForTemplatesCollection] =
		useState(false);
	const { userId } = useSessionData();
	const { setOnboardingState } = useSetOnboardingState();

	const isLicensed = useIsLicensed();
	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();
	const skipConditionsForQueries = !isLicensed || !fg('cc_onboarding_experience');

	const userPersonalizationQueryArgs = {
		variables: {
			userId: userId ?? '',
		},
		skip: skipConditionsForQueries,
	};

	const {
		data: userPersonalizationQueryData,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
		// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	} = expValEquals('confluence_deprecate_experimental_personalization', 'isEnabled', true)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalUserPersonalizationQuery(userPersonalizationQueryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<PersonalizationQuery, PersonalizationQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
				UserPersonalizationQuery,
				userPersonalizationQueryArgs,
			);
	const attributes =
		userPersonalizationQueryData?.experimentalUserPersonalization?.attributes ?? [];

	const isEstablishedUser =
		attributes.find((attr: any) => attr?.name === isEstablishedUserTraitName)?.value === 'true';

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values({
			...TEMPLATES_COLLECTION,
			...ONBOARDING_EXPERIENCES,
		}),
		skipConditionsForQueries,
	);
	// check if user is a founding admin or/and if the user is tagged already
	const { isFoundingAdmin, isTaggedForTemplatesCollectionExperiment, isTemplatesSectionDismissed } =
		deserializeState(onboardingStateData);

	const {
		data: productsTeamAndSpaceQueryData,
		loading: productsTeamAndSpaceQueryLoading,
		error: productsTeamAndSpaceQueryError,
		// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	} = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		GetProductsTeamAndSpaceQuery,
		{
			skip: skipConditionsForQueries || isTemplatesSectionDismissed,
		},
	);

	const hasPersonalSpace = productsTeamAndSpaceQueryData?.user?.confluence?.hasPersonalSpace;

	const isXFlowUser =
		productsTeamAndSpaceQueryData?.confluence_tenantContext?.licensedProducts.some(
			(product: { productKey: string; licenseStatus: string }) =>
				product.productKey.toLowerCase().includes('jira') && product.licenseStatus === 'ACTIVE',
		);

	const isUserEligible = isLicensed && fg('cc_onboarding_experience');

	const isUserEligibleForTemplatesCollectionTagging =
		isUserEligible && isFoundingAdmin && isXFlowUser;

	useEffect(() => {
		if (
			// queries have finished loading
			!userPersonalizationQueryError &&
			!userPersonalizationQueryLoading &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!productsTeamAndSpaceQueryError &&
			!productsTeamAndSpaceQueryLoading &&
			// conditions to meet to be eligible
			!isEstablishedUser &&
			isUserEligibleForTemplatesCollectionTagging &&
			!isTaggedForTemplatesCollectionExperiment
		) {
			// newly tagged user for the experiment
			setisUserEligibleForTemplatesCollection(true);

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'feature',
					attributes: {
						name: 'templatesCollection',
						hasPersonalSpace,
					},
				},
			}).fire();

			void setOnboardingState({
				key: TEMPLATES_COLLECTION.EXPERIMENT_ELIGIBLE,
				value: String(true),
			});
		}
	}, [
		userPersonalizationQueryError,
		userPersonalizationQueryLoading,
		onboardingStateError,
		onboardingStateLoading,
		productsTeamAndSpaceQueryError,
		productsTeamAndSpaceQueryLoading,
		isEstablishedUser,
		isUserEligibleForTemplatesCollectionTagging,
		isTaggedForTemplatesCollectionExperiment,
		setisUserEligibleForTemplatesCollection,
		setOnboardingState,
		createAnalyticsEvent,
		hasPersonalSpace,
	]);

	const isUserPreviouslyTaggedAndEnrolled = Boolean(isTaggedForTemplatesCollectionExperiment);

	const isTemplatesCollectionEligible =
		!isTemplatesSectionDismissed &&
		Boolean(hasPersonalSpace) &&
		(isUserEligibleForTemplatesCollection || isUserPreviouslyTaggedAndEnrolled);

	return {
		isTemplatesCollectionEligible,
		shouldShowGuidingArrow: isTemplatesCollectionEligible && !isEstablishedUser,
		teamType: productsTeamAndSpaceQueryData?.user?.team,
		teamTypeLoading: productsTeamAndSpaceQueryLoading,
		teamTypeError: productsTeamAndSpaceQueryError,
		personalSpaceKey: productsTeamAndSpaceQueryData?.user?.confluence?.space?.key,
	};
};
